import React from "react";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Plus, Trash2, Phone, AlertTriangle } from "lucide-react";
import { QuantityInput } from "../QuantityInput";

export const BlockCentralita = ({
  centralitaId, setCentralitaId,
  centralitaKey,
  extBonos, setExtBonos,
  terminalRows, setTerminalRows,
  ddiQuantity, setDdiQuantity,
  // Multi-discount API (TV-style): every eligible discount renders its own
  // toggle; the parent owns the map keyed by discount product id. Replaces
  // the previous single `centDiscountOn` boolean.
  eligibleCentDiscounts = [],
  centDiscountMap = {}, setCentDiscountMap = () => {},
  cats,
  bonoCentCheck,
  checkDiscountAvailable, currentServices,
  existingCentralitas,
  existingCentralitaBonos, setExistingCentralitaBonos,
  getProduct,
  fmtC,
  renderBlockHeader, renderDiscount,
  openBlocks,
  getPrice,
}) => {
  const priceOf = (p) => (getPrice ? getPrice(p) : (p?.precio_base ?? 0));
  const centralitaProduct = centralitaId ? getProduct(centralitaId) : null;
  const units = centralitaProduct?.unidades_por_seleccion || 1;
  const extensionLineaIds = [];
  if (centralitaId) {
    for (let i = 0; i < units; i++) {
      extensionLineaIds.push(i === 0 ? centralitaKey : `${centralitaKey}_u${i + 1}`);
    }
  }

  return (
    <div className="border border-[#1E1E5F] rounded-lg overflow-hidden" data-testid="block-F">
      {renderBlockHeader("F", <Phone className="h-4 w-4 text-[#03BEE4]" />, "Centralita")}
      {openBlocks.F && (
        <div className="p-4 space-y-3 bg-[#0A0A35]/30">
          <div>
            <Label className="text-slate-300 text-sm">Centralita</Label>
            <Select value={centralitaId || "__none__"} onValueChange={v => setCentralitaId(v === "__none__" ? "" : v)}>
              <SelectTrigger className="bg-[#0A0A35] border-[#1E1E5F] text-white h-9 text-sm" data-testid="centralita-select">
                <SelectValue placeholder="Sin centralita" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A35] border-[#1E1E5F] max-h-60">
                <SelectItem value="__none__" className="text-slate-400 text-sm">— Sin selección —</SelectItem>
                {cats.centralitas.map(p => (
                  <SelectItem key={p.id} value={p.id} className="text-white text-sm">
                    {p.nombre} — {fmtC(priceOf(p))}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {centralitaId && (
            <div className="ml-4 pl-3 border-l-2 border-[#1E1E5F]/50">
              {!bonoCentCheck.permitido ? (
                <p className="text-xs text-red-400/70 flex items-center gap-1 py-1">
                  <AlertTriangle className="h-3 w-3" />
                  {bonoCentCheck.mensaje || "Requiere centralita contratada"}
                </p>
              ) : (
                <div className="space-y-2">
                  {extensionLineaIds.map((lid, idx) => {
                    const raw = extBonos[lid];
                    const selected = Array.isArray(raw) ? raw : (raw ? [raw] : []);
                    const available = cats.bonosCent.filter(b => !selected.includes(b.id));
                    const removeAt = (i) => {
                      const next = selected.filter((_, j) => j !== i);
                      setExtBonos(prev => ({ ...prev, [lid]: next }));
                    };
                    const addOne = (bId) => {
                      if (!bId || bId === "__none__") return;
                      const next = [...selected, bId];
                      setExtBonos(prev => ({ ...prev, [lid]: next }));
                    };
                    return (
                      <div key={lid} className="space-y-1" data-testid={`centralita-ext-${idx}`}>
                        {/* Header line per extension */}
                        <div className="flex items-center gap-2">
                          <span className="text-slate-500 text-xs whitespace-nowrap min-w-[40px]">
                            {units > 1 ? `Ext ${idx + 1}:` : '+'}
                          </span>
                          <Select
                            key={`add-${lid}-${selected.length}`}
                            value="__none__"
                            onValueChange={v => addOne(v)}
                          >
                            <SelectTrigger className="bg-[#0A0A35] border-[#1E1E5F] text-white h-8 text-xs flex-1" data-testid={`centralita-bono-ext-${idx}-add`}>
                              <SelectValue placeholder={selected.length === 0 ? "Añadir bono de minutos" : "Añadir otro bono"} />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0A0A35] border-[#1E1E5F] max-h-60">
                              <SelectItem value="__none__" className="text-slate-400 text-sm">— Seleccionar —</SelectItem>
                              {available.map(b => (
                                <SelectItem key={b.id} value={b.id} className="text-white text-sm">
                                  {b.nombre} — {fmtC(priceOf(b))}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {/* Chosen bonos list */}
                        {selected.map((bId, i) => {
                          const bp = getProduct(bId);
                          if (!bp) return null;
                          return (
                            <div key={`${lid}_${bId}_${i}`} className="flex items-center gap-2 pl-10" data-testid={`centralita-ext-${idx}-bono-${i}`}>
                              <span className="text-cyan-300/80 text-xs flex-1 truncate">└─ {bp.nombre}</span>
                              <span className="text-slate-400 text-xs min-w-[55px] text-right">{fmtC(priceOf(bp))}</span>
                              <button
                                type="button"
                                onClick={() => removeAt(i)}
                                className="text-red-400 hover:text-red-300 p-1"
                                title="Quitar bono"
                                data-testid={`centralita-ext-${idx}-bono-${i}-remove`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div>
            <Label className="text-slate-400 text-xs">Terminales IP</Label>
            <div className="space-y-2 mt-1">
              {terminalRows.map((row, i) => {
                const selected = row.serviceId ? cats.terminals.find(t => t.id === row.serviceId) : null;
                const rowPrice = selected ? priceOf(selected) : 0;
                const qty = Math.max(1, parseInt(row.qty) || 1);
                return (
                  <div key={i} className="flex items-center gap-2 bg-[#1B1B4B]/30 p-2 rounded" data-testid={`terminal-row-${i}`}>
                    <Select
                      value={row.serviceId || "__none__"}
                      onValueChange={v => {
                        const n = [...terminalRows];
                        n[i] = { ...n[i], serviceId: v === "__none__" ? "" : v };
                        setTerminalRows(n);
                      }}
                    >
                      <SelectTrigger className="flex-1 bg-[#0A0A35] border-[#1E1E5F] text-white h-8 text-sm" data-testid={`terminal-select-${i}`}>
                        <SelectValue placeholder="Selecciona terminal..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A35] border-[#1E1E5F] max-h-60">
                        <SelectItem value="__none__" className="text-slate-400 text-sm">— Sin selección —</SelectItem>
                        {cats.terminals.map(t => (
                          <SelectItem key={t.id} value={t.id} className="text-white text-sm">
                            {t.nombre} — {fmtC(priceOf(t))}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <QuantityInput
                      value={row.qty ?? 1}
                      min={1}
                      onChange={(qty) => { const n = [...terminalRows]; n[i] = { ...n[i], qty }; setTerminalRows(n); }}
                      className="w-16 h-8 bg-[#0A0A35] border-[#1E1E5F] text-white text-center text-sm"
                      dataTestId={`terminal-qty-${i}`}
                    />
                    <span className="text-slate-400 text-xs min-w-[60px] text-right">
                      {fmtC(rowPrice * qty)}
                    </span>
                    <button
                      onClick={() => setTerminalRows(terminalRows.filter((_, j) => j !== i))}
                      className="text-red-400 hover:text-red-300 p-1"
                      data-testid={`terminal-remove-${i}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
              {cats.terminals.length > 0 && (
                <Button
                  variant="ghost" size="sm"
                  onClick={() => setTerminalRows([...terminalRows, { serviceId: "", qty: 1 }])}
                  className="text-[#03BEE4] text-xs h-7"
                  data-testid="add-terminal-btn"
                >
                  <Plus className="h-3 w-3 mr-1" />+ Añadir terminal
                </Button>
              )}
            </div>
          </div>

          {cats.ddis.length > 0 && (
            <div className="flex items-center gap-3">
              <Label className="text-slate-400 text-xs whitespace-nowrap">{cats.ddis[0].nombre} — {fmtC(priceOf(cats.ddis[0]))}</Label>
              <QuantityInput value={ddiQuantity || 0} min={0} onChange={setDdiQuantity}
                placeholder="0" dataTestId="ddi-qty-input" className="w-16 h-8 bg-[#0A0A35] border-[#1E1E5F] text-white text-center text-sm" />
              {ddiQuantity > 0 && <span className="text-slate-400 text-xs">{fmtC(priceOf(cats.ddis[0]) * ddiQuantity)}</span>}
            </div>
          )}

          {/* TV-style multi-discount: render every discount product whose
              business rule pairs it with the selected centralita. Each
              toggle's enable state is rule-driven via checkDiscountAvailable
              (requires the rule's producto_requerido to be in current
              services). Rule-only — no hardcoded product names/IDs. */}
          {eligibleCentDiscounts.map(disc => (
            <React.Fragment key={disc.id}>
              {renderDiscount(
                !!centDiscountMap[disc.id],
                (checked) => setCentDiscountMap(prev => ({ ...prev, [disc.id]: checked })),
                disc,
                disc.nombre,
              )}
            </React.Fragment>
          ))}

          {existingCentralitas && existingCentralitas.length > 0 && (
            <div className="mt-3 pt-3 border-t border-[#1E1E5F]/50">
              <Label className="text-slate-500 text-xs uppercase tracking-wider">Ya contratadas</Label>
              {existingCentralitas.map(ec => {
                const ecKey = ec.linea_id || ec.id;
                return (
                  <div key={ec.id} className="space-y-1 mt-2">
                    <div className="flex items-center gap-2 bg-[#1B1B4B]/20 p-2 rounded">
                      <span className="text-slate-300 text-sm flex-1">
                        {ec.nombre_servicio} <span className="text-green-400 text-xs">(activa)</span>
                      </span>
                    </div>
                    {bonoCentCheck.permitido && (
                      <div className="ml-4 pl-3 border-l-2 border-[#1E1E5F]/50">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-500 text-xs whitespace-nowrap">+</span>
                          <Select
                            value={existingCentralitaBonos[ecKey] || "__none__"}
                            onValueChange={v => setExistingCentralitaBonos(prev => ({ ...prev, [ecKey]: v === "__none__" ? "" : v }))}
                          >
                            <SelectTrigger className="bg-[#0A0A35] border-[#1E1E5F] text-white h-8 text-xs flex-1" data-testid={`existing-cent-bono-${ec.id}`}>
                              <SelectValue placeholder="Añadir bono de minutos" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0A0A35] border-[#1E1E5F] max-h-60">
                              <SelectItem value="__none__" className="text-slate-400 text-sm">— Sin bono —</SelectItem>
                              {cats.bonosCent.map(b => (
                                <SelectItem key={b.id} value={b.id} className="text-white text-sm">
                                  {b.nombre} — {fmtC(priceOf(b))}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
